import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";

const contracts = [
  {
    id: "A",
    amount: "38,965",
    currency: "EUR",
    yield: 68,
    days: 3,
    tier: "contract_tier_entry",
    color: "border-amber-400/30",
    glow: "hover:border-amber-400/60",
    badge: "bg-amber-400/10 text-amber-400 border-amber-400/30",
    barColor: "bg-gradient-to-r from-amber-400 to-amber-500",
    icon: "ri-seedling-line",
    popular: false,
  },
  {
    id: "B",
    amount: "50,123",
    currency: "EUR",
    yield: 97,
    days: 3,
    tier: "contract_tier_growth",
    color: "border-orange-400/40",
    glow: "hover:border-orange-400/70",
    badge: "bg-orange-400/10 text-orange-400 border-orange-400/30",
    barColor: "bg-gradient-to-r from-orange-400 to-orange-500",
    icon: "ri-rocket-line",
    popular: true,
  },
  {
    id: "C",
    amount: "72,434",
    currency: "EUR",
    yield: 113,
    days: 3,
    tier: "contract_tier_premium",
    color: "border-white/20",
    glow: "hover:border-white/40",
    badge: "bg-white/10 text-white/70 border-white/20",
    barColor: "bg-gradient-to-r from-white/60 to-white/80",
    icon: "ri-vip-crown-line",
    popular: false,
  },
];

export default function ContractsSection() {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  const selectedContract = contracts.find((c) => c.id === selected);

  const openModal = (id: string) => {
    setSelected(id);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelected(null);
  };

  return (
    <section className="relative py-16 md:py-32 bg-[#060a12] overflow-hidden">
      {/* Background glow */}
      <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 50% 50%, rgba(255,180,0,0.4) 0%, transparent 60%)" }} />
      <div className="absolute inset-0 opacity-5" style={{ backgroundImage: "linear-gradient(rgba(255,200,50,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(255,200,50,0.15) 1px, transparent 1px)", backgroundSize: "60px 60px" }} />

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-12">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-6 md:w-8 h-px bg-amber-400/60" />
            <span className="text-amber-400 text-[10px] md:text-xs tracking-[0.3em] uppercase" style={{ fontFamily: "'Orbitron', sans-serif" }}>
              {t("contract_badge")}
            </span>
            <div className="w-6 md:w-8 h-px bg-amber-400/60" />
          </div>
          <h2 className="text-2xl md:text-5xl font-black text-white mb-3 md:mb-4 leading-tight" style={{ fontFamily: "'Orbitron', sans-serif" }}>
            {t("contract_title_1")}
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-orange-400">
              {t("contract_title_2")}
            </span>
          </h2>
          <p className="text-white/50 text-xs md:text-sm max-w-2xl mx-auto leading-relaxed">
            {t("contract_subtitle")}
          </p>
        </div>

        {/* Collateral info bar */}
        <div className="mb-8 md:mb-14 p-4 md:p-5 border border-amber-400/20 rounded-lg bg-amber-400/[0.03] flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-amber-400/10 border border-amber-400/30 flex-shrink-0">
              <i className="ri-bank-line text-amber-400" />
            </div>
            <div>
              <div className="text-white font-bold text-xs md:text-sm" style={{ fontFamily: "'Orbitron', sans-serif" }}>
                {t("contract_collateral_title")}
              </div>
              <div className="text-white/40 text-[10px] md:text-xs mt-0.5">{t("contract_collateral_sub")}</div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 md:flex md:flex-wrap md:items-center md:gap-6">
            <div className="text-center">
              <div className="text-amber-400 font-black text-lg md:text-xl" style={{ fontFamily: "'Orbitron', sans-serif" }}>$10B</div>
              <div className="text-white/30 text-[9px] md:text-xs">{t("contract_collateral_amount")}</div>
            </div>
            <div className="text-center">
              <div className="text-amber-400 font-black text-lg md:text-xl" style={{ fontFamily: "'Orbitron', sans-serif" }}>$200B</div>
              <div className="text-white/30 text-[9px] md:text-xs">{t("contract_credit_amount")}</div>
            </div>
            <div className="text-center">
              <div className="text-amber-400 font-black text-lg md:text-xl" style={{ fontFamily: "'Orbitron', sans-serif" }}>SpaceX</div>
              <div className="text-white/30 text-[9px] md:text-xs">{t("contract_operator")}</div>
            </div>
          </div>
        </div>

        {/* Contract cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-8 md:mb-12">
          {contracts.map((c) => {
            const returnAmount = ((parseFloat(c.amount.replace(",", "")) * c.yield) / 100).toLocaleString("en-EU", { maximumFractionDigits: 0 });
            const totalAmount = (parseFloat(c.amount.replace(",", "")) * (1 + c.yield / 100)).toLocaleString("en-EU", { maximumFractionDigits: 0 });

            return (
              <div
                key={c.id}
                className={`relative group border ${c.color} ${c.glow} rounded-lg bg-white/[0.02] transition-all duration-300 overflow-hidden`}
              >
                {c.popular && (
                  <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-orange-400 to-transparent" />
                )}
                {c.popular && (
                  <div className="absolute top-3 md:top-4 right-3 md:right-4 px-2 py-0.5 rounded-full text-[9px] md:text-xs bg-orange-400/20 text-orange-400 border border-orange-400/30 tracking-widest uppercase" style={{ fontFamily: "'Orbitron', sans-serif" }}>
                    {t("contract_popular")}
                  </div>
                )}

                <div className="p-5 md:p-7">
                  {/* Tier */}
                  <div className="flex items-center gap-2 mb-4 md:mb-5">
                    <div className={`w-7 h-7 md:w-8 md:h-8 flex items-center justify-center rounded-lg border ${c.badge}`}>
                      <i className={`${c.icon} text-xs md:text-sm`} />
                    </div>
                    <span className={`text-[9px] md:text-xs tracking-widest uppercase border px-2 py-0.5 rounded-full ${c.badge}`} style={{ fontFamily: "'Orbitron', sans-serif" }}>
                      {t(c.tier)}
                    </span>
                  </div>

                  {/* Amount */}
                  <div className="mb-1">
                    <span className="text-white/40 text-[10px] md:text-xs tracking-widest uppercase">{t("contract_investment")}</span>
                  </div>
                  <div className="text-2xl md:text-3xl font-black text-white mb-1" style={{ fontFamily: "'Orbitron', sans-serif" }}>
                    {c.amount}
                    <span className="text-base md:text-lg text-white/40 ml-1">{c.currency}</span>
                  </div>

                  {/* Yield bar */}
                  <div className="my-4 md:my-5">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-white/40 text-[10px] md:text-xs">{t("contract_yield")}</span>
                      <span className="text-amber-400 font-black text-base md:text-lg" style={{ fontFamily: "'Orbitron', sans-serif" }}>
                        +{c.yield}%
                      </span>
                    </div>
                    <div className="h-1.5 md:h-2 bg-white/10 rounded-full overflow-hidden">
                      <div className={`h-full ${c.barColor} rounded-full`} style={{ width: `${Math.min(c.yield, 100)}%` }} />
                    </div>
                  </div>

                  {/* Details */}
                  <div className="space-y-1.5 md:space-y-2 mb-5 md:mb-6">
                    <div className="flex justify-between text-[10px] md:text-xs">
                      <span className="text-white/30">{t("contract_return")}</span>
                      <span className="text-white/70 font-semibold">+{returnAmount} {c.currency}</span>
                    </div>
                    <div className="flex justify-between text-[10px] md:text-xs">
                      <span className="text-white/30">{t("contract_total")}</span>
                      <span className="text-white font-bold">{totalAmount} {c.currency}</span>
                    </div>
                    <div className="flex justify-between text-[10px] md:text-xs">
                      <span className="text-white/30">{t("contract_payout")}</span>
                      <span className="text-emerald-400 font-semibold">{c.days} {t("contract_days")}</span>
                    </div>
                  </div>

                  <button
                    onClick={() => openModal(c.id)}
                    className={`w-full py-2.5 md:py-3 text-[10px] md:text-xs font-bold tracking-widest uppercase rounded-sm transition-all cursor-pointer whitespace-nowrap ${
                      c.popular
                        ? "bg-gradient-to-r from-orange-400 to-amber-400 text-black hover:from-orange-300 hover:to-amber-300"
                        : "border border-white/20 text-white/70 hover:border-amber-400/40 hover:text-amber-400"
                    }`}
                    style={{ fontFamily: "'Orbitron', sans-serif" }}
                  >
                    {t("contract_select")}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Disclaimer */}
        <p className="text-white/20 text-[10px] md:text-xs text-center leading-relaxed max-w-3xl mx-auto">
          {t("contract_disclaimer")}
        </p>
      </div>

      {/* Modal */}
      {showModal && selectedContract && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-0 sm:px-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={closeModal} />
          <div
            ref={modalRef}
            className="relative z-10 w-full sm:max-w-md bg-[#0a0f1e] border border-amber-400/30 rounded-t-2xl sm:rounded-lg p-6 md:p-8"
          >
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-white/40 hover:text-white transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl" />
            </button>

            <div className="text-center mb-5 md:mb-6">
              <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-amber-400/10 border border-amber-400/30 mx-auto mb-3 md:mb-4">
                <i className="ri-information-line text-amber-400 text-lg md:text-xl" />
              </div>
              <h3 className="text-white font-black text-base md:text-lg mb-2" style={{ fontFamily: "'Orbitron', sans-serif" }}>
                {t("contract_modal_title")}
              </h3>
              <p className="text-white/40 text-xs md:text-sm leading-relaxed">{t("contract_modal_desc")}</p>
            </div>

            <div className="p-3 md:p-4 border border-white/10 rounded-lg bg-white/[0.02] mb-5 md:mb-6 space-y-2 md:space-y-3">
              <div className="flex justify-between text-xs md:text-sm">
                <span className="text-white/40">{t("contract_investment")}</span>
                <span className="text-white font-bold">{selectedContract.amount} {selectedContract.currency}</span>
              </div>
              <div className="flex justify-between text-xs md:text-sm">
                <span className="text-white/40">{t("contract_yield")}</span>
                <span className="text-amber-400 font-bold">+{selectedContract.yield}%</span>
              </div>
              <div className="flex justify-between text-xs md:text-sm">
                <span className="text-white/40">{t("contract_payout")}</span>
                <span className="text-emerald-400 font-bold">{selectedContract.days} {t("contract_days")}</span>
              </div>
            </div>

            <p className="text-white/30 text-[10px] md:text-xs text-center leading-relaxed mb-5 md:mb-6">
              {t("contract_modal_note")}
            </p>

            <button
              onClick={closeModal}
              className="w-full py-3 border border-white/20 text-white/60 text-xs font-bold tracking-widest uppercase rounded-sm hover:border-amber-400/40 hover:text-amber-400 transition-all cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Orbitron', sans-serif" }}
            >
              {t("contract_modal_close")}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
